import type { CSSProperties } from 'react';

import { space, type SpaceScale } from './tokens';

/**
 * Layout props — the shared positioning / sizing / overflow / flex-item contract accepted by
 * every layout primitive (`Box`, `Flex`, `Grid`). Modelled on Radix Themes' layout props so a
 * page author reaches for a prop, not `style`, for the one-off geometry that belongs at a call
 * site rather than in a component's own CSS module (ADR 0040).
 *
 * Enum props become `data-*` attributes; length props become `--layout-*` custom properties.
 * Both are consumed by `layout.module.css`, and every rule there is gated on an attribute the
 * primitive emits only when the matching prop is set — so an element that sets none of these is
 * untouched, and a consumer `className` keeps winning on the properties it declares.
 *
 * Deliberately absent (see the ADR): margin props (components do not own outer spacing),
 * responsive object values, and a `display` enum (`Flex` keeps its `inline` boolean).
 */

/**
 * The elements a layout primitive's `as` accepts (ADR 0073): the containers whose attributes are all
 * global, so `React.HTMLAttributes` types them fully. Anything that needs its own attributes or
 * behaviour — a link's `href`, a button's `type`, a label's `htmlFor`, a router link — is rendered
 * through `asChild` instead, where the child element carries its own types.
 */
export type LayoutElement =
    | 'div'
    | 'span'
    | 'p'
    | 'section'
    | 'article'
    | 'aside'
    | 'header'
    | 'footer'
    | 'main'
    | 'nav'
    | 'ul'
    | 'ol'
    | 'li'
    | 'dl'
    | 'form'
    | 'label';

/**
 * The element-choice props every layout primitive takes: `as` for a plain element, `asChild` for anything else.
 * A primitive whose children must be its own (`Grid`, `Grid.Col`) passes a narrower list as `E`.
 */
export interface LayoutElementProps<E extends LayoutElement = LayoutElement> {
    /** HTML element to render. Defaults to `'div'`. Ignored when `asChild` is set. */
    as?: E;
    /**
     * Renders the one child element in place of the primitive's own, with the primitive's class names,
     * attributes, styles, event handlers and ref merged onto it — for a link, a button or any component.
     * The child's own props win where the two conflict.
     */
    asChild?: boolean;
}

export type LayoutPosition = 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky';
export type LayoutOverflow = 'visible' | 'hidden' | 'clip' | 'scroll' | 'auto';

/**
 * A length for an offset, size or flex-basis: a `SpaceScale` token key (resolved to
 * `var(--space-{n})`), or any CSS length / keyword string passed through verbatim
 * (`'50%'`, `'40rem'`, `'auto'`, `'max-content'`). Reach for the token first.
 */
export type LayoutLength = SpaceScale | 'auto' | (string & {});

export interface LayoutProps {
    /** Sets `position`. `relative` is what a Badge host wrapper needs. */
    position?: LayoutPosition;
    /** Sets all four offsets at once; a side-specific offset overrides it. */
    inset?: LayoutLength;
    /** Sets `top`. Overrides `inset`. */
    top?: LayoutLength;
    /** Sets `right`. Overrides `inset`. */
    right?: LayoutLength;
    /** Sets `bottom`. Overrides `inset`. */
    bottom?: LayoutLength;
    /** Sets `left`. Overrides `inset`. */
    left?: LayoutLength;
    /** Sets `width`. */
    width?: LayoutLength;
    /** Sets `min-width`. `minWidth={0}` is the usual fix for a flex child that will not shrink. */
    minWidth?: LayoutLength;
    /** Sets `max-width`. */
    maxWidth?: LayoutLength;
    /** Sets `height`. */
    height?: LayoutLength;
    /** Sets `min-height`. */
    minHeight?: LayoutLength;
    /** Sets `max-height`. */
    maxHeight?: LayoutLength;
    /** Sets `overflow` on both axes. */
    overflow?: LayoutOverflow;
    /** Sets `overflow-x`. Overrides `overflow` on that axis. */
    overflowX?: LayoutOverflow;
    /** Sets `overflow-y`. Overrides `overflow` on that axis. */
    overflowY?: LayoutOverflow;
    /** Sets `flex-grow` — for this element as a **child** of a flex container. */
    flexGrow?: 0 | 1;
    /** Sets `flex-shrink` — for this element as a **child** of a flex container. */
    flexShrink?: 0 | 1;
    /** Sets `flex-basis` — for this element as a **child** of a flex container. */
    flexBasis?: LayoutLength;
}

/** Every key of `LayoutProps`, so a primitive can split them off `...rest`. */
export const LAYOUT_PROP_KEYS = [
    'position',
    'inset',
    'top',
    'right',
    'bottom',
    'left',
    'width',
    'minWidth',
    'maxWidth',
    'height',
    'minHeight',
    'maxHeight',
    'overflow',
    'overflowX',
    'overflowY',
    'flexGrow',
    'flexShrink',
    'flexBasis',
] as const satisfies readonly (keyof LayoutProps)[];

/** The length-valued props, keyed by prop name → the CSS property / `data-layout` word they map to. */
const LENGTH_PROPS = {
    top: 'top',
    right: 'right',
    bottom: 'bottom',
    left: 'left',
    width: 'width',
    minWidth: 'min-width',
    maxWidth: 'max-width',
    height: 'height',
    minHeight: 'min-height',
    maxHeight: 'max-height',
    flexBasis: 'flex-basis',
} as const;

type LengthProp = keyof typeof LENGTH_PROPS;

/**
 * Resolves a `LayoutLength` to a CSS value: a token key becomes its `--space-*` reference,
 * a string passes through, `undefined` stays `undefined` so the caller can omit it.
 */
export const length = (value?: LayoutLength): string | undefined =>
    value == null ? undefined : typeof value === 'number' ? space(value) : value;

/** What a primitive spreads onto its root: the gating attributes and the custom properties. */
export interface ResolvedLayout {
    /** `data-*` attributes — spread onto the element. Unset props are `undefined` (not rendered). */
    attrs: {
        'data-position'?: LayoutPosition;
        'data-overflow'?: LayoutOverflow;
        'data-overflow-x'?: LayoutOverflow;
        'data-overflow-y'?: LayoutOverflow;
        'data-flex-grow'?: 0 | 1;
        'data-flex-shrink'?: 0 | 1;
        /** Space-separated words naming the set length props, matched with `[data-layout~='word']`. */
        'data-layout'?: string;
    };
    /** `--layout-*` custom properties — merge into the element's `style`. */
    vars: CSSProperties;
}

/**
 * Splits the layout props off an arbitrary props object and resolves them to the attributes and
 * custom properties `layout.module.css` consumes. Everything else comes back untouched in `rest`.
 *
 * @example
 * const { attrs, vars, rest } = resolveLayoutProps(others);
 * <Tag {...attrs} className={clsx(styles.box, layoutStyles.layout, className)} style={{ ...vars, ...style }} {...rest} />
 */
export function resolveLayoutProps<P extends LayoutProps>(
    props: P
): ResolvedLayout & { rest: Omit<P, keyof LayoutProps> } {
    const {
        position,
        inset,
        top,
        right,
        bottom,
        left,
        width,
        minWidth,
        maxWidth,
        height,
        minHeight,
        maxHeight,
        overflow,
        overflowX,
        overflowY,
        flexGrow,
        flexShrink,
        flexBasis,
        ...rest
    } = props;

    // Offsets cascade like Box's padding: the shorthand fills any side not set explicitly.
    const lengths: Partial<Record<LengthProp, LayoutLength | undefined>> = {
        top: top ?? inset,
        right: right ?? inset,
        bottom: bottom ?? inset,
        left: left ?? inset,
        width,
        minWidth,
        maxWidth,
        height,
        minHeight,
        maxHeight,
        flexBasis,
    };

    const vars: Record<string, string> = {};
    const words: string[] = [];
    (Object.keys(LENGTH_PROPS) as LengthProp[]).forEach((prop) => {
        const value = length(lengths[prop]);
        if (value !== undefined) {
            const word = LENGTH_PROPS[prop];
            vars[`--layout-${word}`] = value;
            words.push(word);
        }
    });

    return {
        attrs: {
            'data-position': position,
            'data-overflow': overflow,
            'data-overflow-x': overflowX,
            'data-overflow-y': overflowY,
            'data-flex-grow': flexGrow,
            'data-flex-shrink': flexShrink,
            'data-layout': words.length ? words.join(' ') : undefined,
        },
        vars: vars as CSSProperties,
        rest,
    };
}
